import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type TaskQueueResponse } from "../../lib/api";
import { useUser } from "../../lib/auth";
import { timeAgo } from "../../lib/format";
import {
  Card,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  StatusPill,
  useToast,
} from "../../components/ui";

const SLA_TONE = {
  overdue: { bg: "#FFECEC", fg: "#B23438" },
  due_soon: { bg: "#FCF6D2", fg: "#75650B" },
  on_track: { bg: "#EFECEE", fg: "#57515B" },
} as const;

export function TasksScreen() {
  const user = useUser();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => api.get<TaskQueueResponse>("/workflow/tasks"),
  });

  const act = useMutation({
    mutationFn: ({
      documentId,
      action,
      comment,
    }: {
      documentId: string;
      action: string;
      comment?: string;
    }) =>
      api.post<{ message: string }>(
        `/workflow/documents/${documentId}/transition`,
        { action, comment },
      ),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["home"] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Action refused", "danger"),
  });

  const requestChanges = (documentId: string) => {
    const comment = window.prompt(
      "A comment is required when requesting changes. What needs to change?",
    );
    if (!comment?.trim()) return;
    act.mutate({ documentId, action: "REQUEST_CHANGES", comment: comment.trim() });
  };

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  return (
    <>
      <PageHeader
        eyebrow="Queue"
        title="My Tasks"
        subtitle={`Items routed to you as ${user.roleArticleLabel}`}
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
        <div className="space-y-3">
          {data?.items.length === 0 && (
            <Card>
              <Empty
                title="Nothing is waiting on you"
                detail={
                  user.canReview
                    ? "Documents appear here when they reach review or approval."
                    : "Documents appear here when they are returned to you with requested changes."
                }
              />
            </Card>
          )}

          {data?.items.map((task) => {
            const tone = SLA_TONE[task.slaState];
            return (
              <Card key={task.document.id}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <StatusPill
                    status={task.document.status}
                    label={task.document.statusLabel}
                  />
                  <span className="pill" style={{ background: tone.bg, color: tone.fg }}>
                    {task.slaLabel}
                  </span>
                  <span className="text-2xs text-muted-soft">
                    {task.document.versionLabel} ·{" "}
                    {task.document.owner?.displayName ?? "unassigned"}
                    {task.stageName && ` · ${task.stageName}`}
                  </span>
                  <span className="ml-auto text-2xs text-muted-soft">
                    updated {timeAgo(task.document.updatedAt)}
                  </span>
                </div>

                <Link
                  to={`/documents/${task.document.id}`}
                  className="block text-md font-bold text-ink no-underline hover:underline"
                >
                  {task.document.title}
                </Link>
                <div className="mt-0.5 text-2xs text-muted-soft">
                  {task.document.docNo}
                </div>
                {task.changeNote && (
                  <p className="mt-2 text-sm text-muted-strong">{task.changeNote}</p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {task.primaryAction && (
                    <button
                      className="btn-accent"
                      disabled={act.isPending}
                      onClick={() =>
                        act.mutate({
                          documentId: task.document.id,
                          action: task.primaryAction!,
                        })
                      }
                    >
                      {task.primaryActionLabel}
                    </button>
                  )}
                  {user.canReview && (
                    <button
                      className="btn-ghost"
                      disabled={act.isPending}
                      onClick={() => requestChanges(task.document.id)}
                    >
                      Request changes
                    </button>
                  )}
                  <Link
                    to={`/documents/${task.document.id}`}
                    className="btn-quiet no-underline"
                  >
                    Open
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>

        <Card title="Queue summary" collapsible storageKey="queue-summary" defaultOpen={false}>
          <dl className="space-y-2.5">
            {data?.summary.map((row) => (
              <div key={row.key} className="flex items-baseline justify-between gap-3">
                <dt className="text-sm text-muted">{row.key}</dt>
                <dd className="text-md font-bold text-ink">{row.value}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 border-t border-line-soft pt-3 text-2xs leading-relaxed text-muted-soft">
            Publishing writes back to the library and re-indexes the document
            immediately. A stage past its SLA is escalated to a second Network
            Control Manager automatically.
          </p>
        </Card>
      </div>
    </>
  );
}
